import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
var __jsx = React.createElement;
import React, { useCallback, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_API_MAP_KEY } from "@globalConfig";
import { Icon } from "@syarahtech/ui";
var mapContainerStyle = {
  width: '100%',
  height: '100%'
};
var libraries = ['places', 'geometry'];
var LocationMapModal = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var initialCenter = _ref.initialCenter,
    onCenterChange = _ref.onCenterChange,
    onConfirm = _ref.onConfirm,
    onLocationChange = _ref.onLocationChange,
    onLocationDenied = _ref.onLocationDenied;
  var _useState = useState(GOOGLE_API_MAP_KEY),
    apiKey = _useState[0];
  var mapRef = useRef(null);
  var _useState2 = useState(false),
    isLoadingDetails = _useState2[0],
    setIsLoadingDetails = _useState2[1];
  var _useState3 = useState(false),
    locationDenied = _useState3[0],
    setLocationDenied = _useState3[1];
  // Store initial center in state to prevent re-renders from resetting the map
  var _useState4 = useState(function () {
      return {
        lat: initialCenter.lat,
        lng: initialCenter.lng
      };
    }),
    mapCenter = _useState4[0],
    setMapCenter = _useState4[1];
  var _useState5 = useState({
      lat: initialCenter.lat,
      lng: initialCenter.lng,
      details: null
    }),
    selectedLocation = _useState5[0],
    setSelectedLocation = _useState5[1];

  // Reverse geocoding function (declared early for use in captureUserLocation)
  var reverseGeocodeLocation = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(lat, lng) {
      var geocoder, response, result, components, country, city, neighbourhood, street, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            if (!(!window.google || !window.google.maps)) {
              _context.n = 1;
              break;
            }
            return _context.a(2, null);
          case 1:
            _context.p = 1;
            geocoder = new window.google.maps.Geocoder();
            _context.n = 2;
            return new Promise(function (resolve, reject) {
              geocoder.geocode({
                location: {
                  lat: lat,
                  lng: lng
                },
                language: 'ar'
              }, function (results, status) {
                if (status === 'OK') {
                  resolve({
                    results: results || []
                  });
                } else {
                  reject(new Error("Geocoding failed: ".concat(status)));
                }
              });
            });
          case 2:
            response = _context.v;
            if (!(response.results && response.results.length > 0)) {
              _context.n = 3;
              break;
            }
            result = response.results[0];
            components = result.address_components;
            country = '';
            city = '';
            neighbourhood = '';
            street = '';
            components === null || components === void 0 ? void 0 : components.forEach(function (component) {
              var types = component.types;
              if (types.includes('country')) {
                country = component.long_name;
              } else if (types.includes('administrative_area_level_1') || types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('sublocality') || types.includes('neighborhood')) {
                neighbourhood = component.long_name;
              } else if (types.includes('route')) {
                street = component.long_name;
              }
            });
            return _context.a(2, {
              country: country,
              city: city,
              neighbourhood: neighbourhood,
              street: street,
              formatted_address: result.formatted_address
            });
          case 3:
            _context.n = 5;
            break;
          case 4:
            _context.p = 4;
            _t = _context.v;
            console.error('Reverse geocoding error:', _t);
          case 5:
            return _context.a(2, null);
        }
      }, _callee, null, [[1, 4]]);
    }));
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }(), []);

  // Function to capture user's current location
  var captureUserLocation = useCallback(function () {
    var isUserInitiated = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(/*#__PURE__*/function () {
        var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2(position) {
          var location, details;
          return _regenerator().w(function (_context2) {
            while (1) switch (_context2.n) {
              case 0:
                location = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                };
                setMapCenter(location);
                if (mapRef.current) {
                  mapRef.current.panTo(location);
                  mapRef.current.setZoom(15);
                }
                setLocationDenied(false);

                // Get location details via reverse geocoding
                setIsLoadingDetails(true);
                onLocationChange === null || onLocationChange === void 0 ? void 0 : onLocationChange(false);
                _context2.n = 1;
                return reverseGeocodeLocation(location.lat, location.lng);
              case 1:
                details = _context2.v;
                setSelectedLocation({
                  lat: location.lat,
                  lng: location.lng,
                  details: details
                });
                setIsLoadingDetails(false);
                onLocationChange === null || onLocationChange === void 0 ? void 0 : onLocationChange(!!details);
              case 2:
                return _context2.a(2);
            }
          }, _callee2);
        }));
        return function (_x3) {
          return _ref3.apply(this, arguments);
        };
      }(), function (error) {
        console.error('Geolocation error:', error);
        setLocationDenied(true);
        // Only show toast when user clicks the button, not on initial load
        if (isUserInitiated) {
          onLocationDenied === null || onLocationDenied === void 0 ? void 0 : onLocationDenied(true);
        }
      }, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      });
    }
  }, [onLocationDenied, onLocationChange, reverseGeocodeLocation]);

  // Expose panTo and getCurrentLocation methods to parent
  useImperativeHandle(ref, function () {
    return {
      panTo: function panTo(lat, lng, details) {
        if (mapRef.current) {
          mapRef.current.panTo({
            lat: lat,
            lng: lng
          });
          mapRef.current.setZoom(15);
        }
        // Update selected location with the new coordinates and details
        if (details) {
          setSelectedLocation({
            lat: lat,
            lng: lng,
            details: details
          });
          onLocationChange === null || onLocationChange === void 0 ? void 0 : onLocationChange(true);
        }
      },
      getCurrentLocation: function getCurrentLocation() {
        return selectedLocation;
      }
    };
  });
  var _useJsApiLoader = useJsApiLoader({
      googleMapsApiKey: apiKey,
      libraries: libraries
    }),
    isLoaded = _useJsApiLoader.isLoaded,
    loadError = _useJsApiLoader.loadError;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    map = _React$useState2[0],
    setMap = _React$useState2[1];
  var onLoad = React.useCallback(function callback(map) {
    // This is just an example of getting and using the map instance!!! don't just blindly copy!
    var bounds = new window.google.maps.LatLngBounds(initialCenter);
    map.fitBounds(bounds);
    setMap(map);
  }, []);
  var onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);
  var onMapLoad = useCallback(function (map) {
    mapRef.current = map;

    // Auto-trigger location request when map loads
    captureUserLocation();
  }, [captureUserLocation]);
  var updateLocationWithDetails = useCallback(/*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(lat, lng) {
      var details, newLocation;
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.n) {
          case 0:
            setIsLoadingDetails(true);
            onLocationChange === null || onLocationChange === void 0 ? void 0 : onLocationChange(false); // Notify parent that we're loading
            _context3.n = 1;
            return reverseGeocodeLocation(lat, lng);
          case 1:
            details = _context3.v;
            newLocation = {
              lat: lat,
              lng: lng,
              details: details
            };
            setSelectedLocation(newLocation);
            setIsLoadingDetails(false);
            onLocationChange === null || onLocationChange === void 0 ? void 0 : onLocationChange(!!details); // Notify parent if we have valid location details
          case 2:
            return _context3.a(2);
        }
      }, _callee3);
    }));
    return function (_x4, _x5) {
      return _ref4.apply(this, arguments);
    };
  }(), [reverseGeocodeLocation, onLocationChange]);
  var _onDragEnd = useCallback(function (map) {
    var center = map.getCenter();
    if (center) {
      updateLocationWithDetails(center.lat(), center.lng());
    }
  }, [updateLocationWithDetails]);
  return isLoaded ? /*#__PURE__*/React.createElement("div", {
    className: "relative w-full h-full"
  }, /*#__PURE__*/React.createElement(GoogleMap, {
    mapContainerStyle: mapContainerStyle,
    center: mapCenter,
    zoom: 16,
    onLoad: onMapLoad,
    onDragEnd: function onDragEnd() {
      if (mapRef.current) {
        _onDragEnd(mapRef.current);
      }
    },
    options: {
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: false,
      mapTypeId: 'roadmap',
      clickableIcons: false
    }
  }, /*#__PURE__*/React.createElement(React.Fragment, null)), /*#__PURE__*/React.createElement("div", {
    className: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full pointer-events-none z-10"
  }, /*#__PURE__*/React.createElement("svg", {
    width: "48",
    height: "48",
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M24 4C16.268 4 10 10.268 10 18C10 28.5 24 44 24 44C24 44 38 28.5 38 18C38 10.268 31.732 4 24 4ZM24 23C21.239 23 19 20.761 19 18C19 15.239 21.239 13 24 13C26.761 13 29 15.239 29 18C29 20.761 26.761 23 24 23Z",
    fill: "#22C55E",
    stroke: "white",
    strokeWidth: "2"
  }))), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      return captureUserLocation(true);
    },
    className: "absolute bottom-[120px] right-2.5 bg-white border-2 border-solid border-gray-300 rounded-lg shadow-md cursor-pointer w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors z-10",
    title: "\u0627\u0646\u062A\u0642\u0644 \u0625\u0644\u0649 \u0645\u0648\u0642\u0639\u064A",
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--my-location",
    width: 5,
    height: 5,
    className: "text-primary-new"
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null);
});
LocationMapModal.displayName = 'LocationMapModal';
export default /*#__PURE__*/React.memo(LocationMapModal);